import { List, Show, Create, Edit, Datagrid, TextField, NumberField, DateField, ChipField, SimpleForm, TextInput, SelectInput, Button, Filter, SearchInput, useListContext } from 'react-admin';
import { Box, Typography, Paper, Grid, Alert, Chip, Stepper, Step, StepLabel } from '@mui/material';
import { Link } from 'react-router-dom';
import { AccountBalance, Receipt, CheckCircle, Lock, TrendingUp, AttachMoney, CalendarToday } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { StatementDrawer } from '@/ledger/account/StatementDrawer';
import type { LedgerAccount } from '@/types/ledger';

const CHART_TYPES = ['Asset', 'Liability', 'Equity', 'Income', 'Expense'];
const ACCOUNT_STATUS = ['Active', 'Frozen', 'Closed'];

export const AccountList = () => (
  <List filters={<AccountFilters />}>
    <AccountListHeader />
    <Datagrid rowClick="show">
      <TextField source="accountNumber" label="Account #" />
      <TextField source="name" />
      <TextField source="chartType" label="Type" />
      <NumberField source="balance" label="Balance" />
      <ChipField source="status" />
    </Datagrid>
  </List>
);

const AccountListHeader = () => {
  const { data: accounts } = useListContext();
  const total = accounts?.length ?? 0;
  const active = accounts?.filter((a: LedgerAccount) => a.status === 'Active').length ?? 0;

  const chartTypeCounts: Record<string, number> = {};
  accounts?.forEach((a: LedgerAccount) => {
    const ct = a.chartType ?? 'Unknown';
    chartTypeCounts[ct] = (chartTypeCounts[ct] || 0) + 1;
  });
  const pieData = Object.entries(chartTypeCounts).map(([label, value]) => ({ label, value }));

  return (
    <ListPageHeader
      title="Chart of Accounts"
      metrics={[
        { label: 'Total Accounts', value: String(total), color: 'primary.main' },
        { label: 'Active', value: String(active), color: 'success.main' },
        { label: 'Asset Accounts', value: String(accounts?.filter((a: LedgerAccount) => a.chartType === 'Asset').length ?? 0), color: 'info.main' },
      ]}
      pieChartData={pieData}
      pieChartTitle="By Chart Type"
    />
  );
};

export const AccountFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search account name or number" />
    <SelectInput source="chartType" choices={CHART_TYPES.map(t => ({ id: t, name: t }))} />
    <SelectInput source="status" choices={ACCOUNT_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
  </Filter>
);

export const AccountShow = () => (
  <Show render={({ record }) => {
    if (!record) return null;
    const isFrozen = record.status === 'Frozen';
    const isClosed = record.status === 'Closed';
    const isActive = record.status === 'Active';

    return (
      <Box sx={{ p: 3 }}>
        {isFrozen && <Alert severity="warning" sx={{ mb: 2 }} icon={<Lock />}>Account frozen — rejects new journal entries</Alert>}
        {isClosed && <Alert severity="error" sx={{ mb: 2 }} icon={<Lock />}>Account closed — excluded from balance derivations</Alert>}
        {isActive && <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>Account active and operational</Alert>}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {([
            { label: 'Account Number', value: record.accountNumber, bold: true },
            { label: 'Account Name', value: record.name },
            { label: 'Chart Type', value: record.chartType },
            { label: 'Status', value: record.status, chip: true },
            { label: 'Balance', value: `R ${(record as { balance?: number }).balance?.toLocaleString() || '0'}`, highlight: true },
            { label: 'Normal Balance', value: record.normalBalance },
            { label: 'Namespace', value: record.namespace || '—' },
            { label: 'Owner', value: record.owner?.id ?? '—' },
          ] as Array<{ label: string; value: string | number | undefined; bold?: boolean; chip?: boolean }>).map(f => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                {f.chip ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={f.value} size="small" color={record.status === 'Active' ? 'success' : record.status === 'Frozen' ? 'warning' : 'error'} />
                  </Box>
                ) : (
                  <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5 }}>{f.value ?? '—'}</Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Account Overview</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AccountBalance color="primary" fontSize="large" />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    R {(record as { balance?: number }).balance?.toLocaleString() || '0.00'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {record.chartType} account — {record.status}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Account Details</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isActive ? <CheckCircle color="success" fontSize="small" /> : <Lock color="warning" fontSize="small" />}
                  <Typography variant="body2">Status: {record.status}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp fontSize="small" color="action" />
                  <Typography variant="body2">Type: {record.chartType}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoney fontSize="small" color="action" />
                  <Typography variant="body2">Currency: {(record as { currency?: { native?: string } }).currency?.native ?? 'ZAR'}</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <StatementDrawer accountId={(record as { id?: string }).id ?? (record as { _id?: string })._id ?? ''} />
          <Button component={Link} to="/journal-entries" variant="outlined" size="small" startIcon={<Receipt />}>
            View Journal Entries
          </Button>
          <Button component={Link} to="/invoices" variant="outlined" size="small" startIcon={<Receipt />}>
            View Invoices
          </Button>
          <Button component={Link} to="/receipts" variant="outlined" size="small" startIcon={<Receipt />}>
            View Receipts
          </Button>
        </Box>
      </Box>
    );
  }} />
);

export const AccountCreate = () => (
  <Create>
    <SimpleForm>
      <Alert severity="info" sx={{ mb: 2 }}>
        Account numbers are human-readable identifiers — categorization determined by chartType
      </Alert>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="accountNumber" label="Account Number" fullWidth helperText="Unique identifier" />
        <TextInput source="name" label="Account Name" fullWidth />
        <SelectInput source="chartType" choices={CHART_TYPES.map(t => ({ id: t, name: t }))} fullWidth />
        <SelectInput source="status" choices={ACCOUNT_STATUS.map(s => ({ id: s, name: s }))} fullWidth defaultValue="Active" />
        <TextInput source="namespace" label="Namespace" fullWidth />
      </Box>
    </SimpleForm>
  </Create>
);

export const AccountEdit = () => (
  <Edit>
    <SimpleForm>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Freezing or closing an account requires zero balance
      </Alert>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="accountNumber" label="Account Number" fullWidth />
        <TextInput source="name" label="Account Name" fullWidth />
        <SelectInput source="chartType" choices={CHART_TYPES.map(t => ({ id: t, name: t }))} fullWidth />
        <SelectInput source="status" choices={ACCOUNT_STATUS.map(s => ({ id: s, name: s }))} fullWidth />
        <TextInput source="namespace" label="Namespace" fullWidth />
      </Box>
    </SimpleForm>
  </Edit>
);

export const CheckpointList = () => (
  <List>
    <Datagrid rowClick="show">
      <TextField source="checkpointId" label="Checkpoint #" />
      <TextField source="accountId" label="Account" />
      <NumberField source="balance" label="Balance (ZAR)" />
      <DateField source="asOfDate" label="As At" />
    </Datagrid>
  </List>
);

export const CheckpointShow = () => (
  <Show render={({ record }) => {
    if (!record) return null;
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }} icon={<CalendarToday />}>
          Checkpoint created at {record.asOfDate} — balance is point-in-time snapshot
        </Alert>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Account', value: record.accountId },
            { label: 'Balance', value: `R ${record.balance?.toLocaleString() || '0'}`, highlight: true },
            { label: 'As Of Date', value: record.asOfDate },
            { label: 'Currency', value: record.currency },
            { label: 'Type', value: record.checkpointType },
            { label: 'Description', value: record.description ?? '—' },
            { label: 'Entry Count', value: record.entryCount },
            { label: 'Debit Total', value: `R ${record.debitTotal?.toLocaleString() ?? '0'}` },
            { label: 'Credit Total', value: `R ${record.creditTotal?.toLocaleString() ?? '0'}` },
          ].map(f => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                <Typography variant="body1" sx={{ fontWeight: f.highlight ? 700 : 400, mt: 0.5, color: f.highlight ? 'success.main' : 'inherit' }}>{f.value ?? '—'}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button component={Link} to="/accounts" variant="outlined" size="small">View Account</Button>
          <Button component={Link} to="/journal-entries" variant="outlined" size="small">View Entries</Button>
        </Box>
      </Box>
    );
  }} />
);

export const CheckpointCreate = () => (
  <Create>
    <SimpleForm>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="accountId" label="Account ID" fullWidth />
        <TextInput source="checkpointType" label="Checkpoint Type" fullWidth defaultValue="Manual" />
        <NumberField source="balance" label="Balance (ZAR)" />
        <DateField source="asOfDate" label="As At Timestamp" />
      </Box>
    </SimpleForm>
  </Create>
);
